import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { PhoneIcon, MapPinIcon, ClockIcon, CheckIcon, FireIcon } from './Icons';
import { XIcon } from 'lucide-react';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <div style={{
    position: 'fixed',
    inset: 0,
    zIndex: 9999
  }} onClick={onClose} data-id="element-151">
      <div style={{
      position: 'absolute',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(4px)'
    }} data-id="element-152" />
      <div style={{
      position: 'relative',
      zIndex: 1
    }} className="flex items-start justify-center min-h-screen py-8 px-4 overflow-y-auto" data-id="element-153">
        <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.3
      }} onClick={e => e.stopPropagation()} className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-3xl shadow-2xl my-8" data-id="element-154">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-155">
            <div className="flex items-center gap-3" data-id="element-156">
              <div className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center" data-id="element-157">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-brand-red" data-id="element-158">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" data-id="element-159" />
                </svg>
              </div>
              <h2 className="font-oswald text-xl font-bold text-white uppercase tracking-wide" data-id="element-160">
                {title}
              </h2>
            </div>
            <button onClick={onClose} className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-id="element-161">
              <XIcon className="w-5 h-5" data-id="element-162" />
            </button>
          </div>
          {/* Content */}
          <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto legal-content" data-id="element-163">
            {children}
          </div>
          {/* Footer */}
          <div className="p-6 border-t border-white/10 flex items-center justify-between" data-id="element-164">
            <p className="text-gray-500 text-xs" data-id="element-165">
              © {new Date().getFullYear()} Move Fighters LLC
            </p>
            <button onClick={onClose} className="bg-brand-red hover:bg-brand-darkred text-white px-6 py-2.5 rounded-xl font-oswald font-bold text-sm uppercase tracking-wider transition-colors" data-id="element-166">
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="font-oswald text-lg font-bold text-brand-red uppercase tracking-wide mt-8 mb-4 flex items-center gap-2" data-id="element-167">
      <span className="w-1.5 h-6 bg-brand-red rounded-full" data-id="element-168" />
      {children}
    </h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-300 text-sm leading-relaxed mb-4" data-id="element-169">{children}</p>;
}
function BulletItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-3 text-sm text-gray-300 mb-3" data-id="element-170">
      <CheckIcon className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" data-id="element-171" />
      <span className="leading-relaxed" data-id="element-172">{children}</span>
    </li>;
}
function TermsContent() {
  return <div data-id="element-173">
      <Paragraph data-id="element-174">
        Welcome to Move Fighters. By purchasing a moving package through our
        website, you ("the Client") agree to the following terms and conditions.
        These terms govern the relationship between you and Move Fighters LLC
        ("the Company") and are designed to ensure a smooth, transparent, and
        professional moving experience for all parties.
      </Paragraph>

      <SectionTitle data-id="element-175">1. Service Agreement</SectionTitle>
      <Paragraph data-id="element-176">
        When you purchase a Move Fighters package, you are reserving a dedicated
        crew, truck, and logistics team exclusively for your scheduled move
        date. Upon payment, we immediately block that date on our calendar and
        decline other service requests for that time slot. This commitment is
        mutual — we guarantee our team will be ready, and you commit to the
        scheduled date and terms outlined herein.
      </Paragraph>

      <SectionTitle data-id="element-177">2. Fixed Pricing Guarantee</SectionTitle>
      <Paragraph data-id="element-178">
        All Move Fighters packages are sold at a fixed price. The price you see
        is the price you pay — no hidden fees, no hourly surprises, no fuel
        surcharges, and no stair charges. Your package includes everything
        listed in the package description at the time of purchase.
      </Paragraph>

      <SectionTitle data-id="element-179">3. Payment Terms</SectionTitle>
      <Paragraph data-id="element-180">
        Full payment is required at the time of booking to secure your rescue
        date. Payment is processed securely through our online platform. By
        completing payment, you acknowledge that you have read and agree to all
        terms, conditions, and policies outlined on this page.
      </Paragraph>

      <SectionTitle data-id="element-181">4. Client Responsibilities</SectionTitle>
      <ul className="mb-4" data-id="element-182">
        <BulletItem data-id="element-183">
          Provide accurate pickup and delivery addresses at the time of booking.
        </BulletItem>
        <BulletItem data-id="element-184">
          Ensure access to both locations on the scheduled move date (keys, gate
          codes, elevator reservations, etc.).
        </BulletItem>
        <BulletItem data-id="element-185">
          Have all items ready and accessible for the moving crew upon arrival.
        </BulletItem>
        <BulletItem data-id="element-186">
          Disclose any special items (pianos, safes, antiques) that may require
          additional handling.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-187">5. Service Coverage</SectionTitle>
      <Paragraph data-id="element-188">
        Move Fighters operates within a 50-mile radius of Tampa, FL. Both pickup
        and delivery addresses must fall within our service area. If either
        address is outside our coverage zone, the booking may be subject to
        cancellation or adjustment.
      </Paragraph>

      <SectionTitle data-id="element-189">6. Liability & Insurance</SectionTitle>
      <Paragraph data-id="element-190">
        Move Fighters is fully licensed and insured. We carry comprehensive
        liability coverage for your belongings during transit. In the unlikely
        event of damage, claims must be reported within 24 hours of delivery
        completion. Our team will work with you to resolve any issues promptly
        and fairly.
      </Paragraph>

      <SectionTitle data-id="element-191">7. Governing Law</SectionTitle>
      <Paragraph data-id="element-192">
        These terms are governed by the laws of the State of Florida. Any
        disputes arising from these terms shall be resolved in the courts of
        Hillsborough County, Florida.
      </Paragraph>

      <div className="mt-8 p-4 bg-brand-red/5 border border-brand-red/20 rounded-xl" data-id="element-193">
        <p className="text-gray-400 text-xs leading-relaxed" data-id="element-194">
          <strong className="text-white" data-id="element-195">Last Updated:</strong> April 2026. Move
          Fighters LLC reserves the right to update these terms at any time.
          Continued use of our services constitutes acceptance of the most
          current version of these terms.
        </p>
      </div>
    </div>;
}
function CancellationContent() {
  return <div data-id="element-196">
      <div className="p-4 bg-white/5 border border-white/10 rounded-xl mb-6" data-id="element-197">
        <p className="text-gray-300 text-sm leading-relaxed" data-id="element-198">
          At Move Fighters, we operate as a rapid-response rescue unit. When you
          purchase your moving package, we reserve a team of specialists, a
          truck, and dedicated logistics resources exclusively for you —
          blocking that space on our calendar and turning away other clients.
          The following policies reflect our mutual commitment to a successful
          move.
        </p>
      </div>

      <SectionTitle data-id="element-199">1. Date Changes & Rescheduling</SectionTitle>
      <Paragraph data-id="element-200">
        We understand that closing dates and key handovers can shift. That's why
        we offer flexibility under the following guidelines:
      </Paragraph>
      <ul className="mb-4" data-id="element-201">
        <BulletItem data-id="element-202">
          <strong className="text-white" data-id="element-203">Free Date Changes:</strong> You are
          entitled to two (2) complimentary date changes, provided each request
          is made at least 72 hours before the scheduled move date.
        </BulletItem>
        <BulletItem data-id="element-204">
          <strong className="text-white" data-id="element-205">Rescheduling Fee:</strong> Starting
          from the third date change, or any change requested within the 72-hour
          window, a $100.00 USD administrative fee will apply to cover logistics
          and scheduling adjustments.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-206">2. Cancellation & Refund Policy</SectionTitle>
      <Paragraph data-id="element-207">
        Our operational costs — including payment processing, crew scheduling,
        route planning, and equipment allocation — begin the moment your payment
        is confirmed:
      </Paragraph>
      <ul className="mb-4" data-id="element-208">
        <BulletItem data-id="element-209">
          <strong className="text-white" data-id="element-210">
            Cancellation with 72+ hours notice:
          </strong>{' '}
          A refund of 70% of the total amount paid will be issued. The remaining
          30% is retained by the Company to cover operational expenses,
          administrative costs, payment processing fees, and the reserved
          calendar slot.
        </BulletItem>
        <BulletItem data-id="element-211">
          <strong className="text-white" data-id="element-212">
            Cancellation with less than 72 hours notice:
          </strong>{' '}
          No refund will be issued. At this point, the Company has committed
          100% of planning resources and forfeited the opportunity to serve
          other clients on that date.
        </BulletItem>
        <BulletItem data-id="element-213">
          <strong className="text-white" data-id="element-214">On-Site Cancellation:</strong> If our
          crew arrives at the scheduled location and the move cannot be
          completed due to circumstances beyond Move Fighters' control (client
          is not present, no access/keys available, client changes their mind
          on-site), no refund of any kind will be issued.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-215">3. Chargeback & Dispute Policy</SectionTitle>
      <Paragraph data-id="element-216">
        By completing your purchase on our platform, you explicitly acknowledge
        that you have read, understood, and agreed to these cancellation and
        refund policies.
      </Paragraph>
      <ul className="mb-4" data-id="element-217">
        <BulletItem data-id="element-218">
          Move Fighters employs identity and location verification systems for
          every transaction.
        </BulletItem>
        <BulletItem data-id="element-219">
          Chargebacks are not permitted for services cancelled outside of our
          refund policy. Any chargeback attempt based on a denied date change or
          late cancellation will be formally disputed, and this signed digital
          acceptance of terms will be submitted as evidence to the financial
          institution.
        </BulletItem>
        <BulletItem data-id="element-220">
          Any disagreements must be resolved directly with our customer service
          team through official channels before contacting your bank or credit
          card company.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-221">4. Logistics & Coordination</SectionTitle>
      <Paragraph data-id="element-222">
        To ensure the efficiency of your Rescue Unit, we do not accept bookings
        with less than 72 hours of lead time. Once your purchase is confirmed,
        our specialists will contact you to verify the exact pickup and delivery
        coordinates. It is the client's responsibility to provide accurate
        information for a successful operation.
      </Paragraph>

      <div className="mt-8 p-4 bg-brand-yellow/5 border border-brand-yellow/20 rounded-xl" data-id="element-223">
        <p className="text-brand-yellow text-xs font-bold uppercase tracking-wider mb-2" data-id="element-224">
          Important Notice
        </p>
        <p className="text-gray-400 text-xs leading-relaxed" data-id="element-225">
          During the checkout process, you will be required to confirm
          acceptance of this Cancellation & Refund Policy before completing your
          purchase. A copy of these terms will also be included in your order
          confirmation email. This ensures full transparency and serves as
          documentation for both parties.
        </p>
      </div>
    </div>;
}
function PrivacyContent() {
  return <div data-id="element-226">
      <Paragraph data-id="element-227">
        Move Fighters LLC ("we," "us," or "our") is committed to protecting your
        privacy. This Privacy Policy explains how we collect, use, and safeguard
        your personal information when you use our website and services.
      </Paragraph>

      <SectionTitle data-id="element-228">1. Information We Collect</SectionTitle>
      <ul className="mb-4" data-id="element-229">
        <BulletItem data-id="element-230">
          <strong className="text-white" data-id="element-231">Contact Information:</strong> Name,
          phone number, email address.
        </BulletItem>
        <BulletItem data-id="element-232">
          <strong className="text-white" data-id="element-233">Address Information:</strong> Pickup
          and delivery addresses for your move.
        </BulletItem>
        <BulletItem data-id="element-234">
          <strong className="text-white" data-id="element-235">Payment Information:</strong>{' '}
          Credit/debit card details processed securely through our payment
          provider.
        </BulletItem>
        <BulletItem data-id="element-236">
          <strong className="text-white" data-id="element-237">Move Details:</strong> Preferred dates,
          special instructions, and package selection.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-238">2. How We Use Your Information</SectionTitle>
      <ul className="mb-4" data-id="element-239">
        <BulletItem data-id="element-240">
          To process and fulfill your moving service booking.
        </BulletItem>
        <BulletItem data-id="element-241">
          To communicate with you about your scheduled move (confirmations,
          reminders, updates).
        </BulletItem>
        <BulletItem data-id="element-242">
          To improve our services and customer experience.
        </BulletItem>
        <BulletItem data-id="element-243">
          To comply with legal obligations and resolve disputes.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-244">3. Data Security</SectionTitle>
      <Paragraph data-id="element-245">
        We implement industry-standard security measures to protect your
        personal information. Payment data is encrypted and processed through
        PCI-compliant payment processors. We never store your full credit card
        number on our servers.
      </Paragraph>

      <SectionTitle data-id="element-246">4. Third-Party Sharing</SectionTitle>
      <Paragraph data-id="element-247">
        We do not sell, trade, or rent your personal information to third
        parties. We may share limited information with trusted service partners
        (payment processors, communication tools) solely for the purpose of
        delivering our services to you.
      </Paragraph>

      <SectionTitle data-id="element-248">5. Your Rights</SectionTitle>
      <Paragraph data-id="element-249">
        You have the right to request access to, correction of, or deletion of
        your personal data. To exercise these rights, contact us at
        info@movefighters.com or call (813) 476-8935.
      </Paragraph>

      <SectionTitle data-id="element-250">6. Contact Us</SectionTitle>
      <Paragraph data-id="element-251">
        If you have questions about this Privacy Policy, please contact us at
        info@movefighters.com or (813) 476-8935. Move Fighters LLC, Tampa, FL.
      </Paragraph>
    </div>;
}
function AboutContent() {
  return <div data-id="element-252">
      <Paragraph data-id="element-253">
        Move Fighters was born from a simple frustration: the moving industry is
        broken. Hidden fees, slow crews, damaged furniture, and "low-ball"
        quotes that triple by the end of the day. We decided to fix it —
        permanently.
      </Paragraph>

      <SectionTitle data-id="element-254">Our Mission</SectionTitle>
      <Paragraph data-id="element-255">
        We treat every move like a rescue operation. Our crews arrive on time,
        in uniform, with professional-grade equipment — and the price you see is
        the price you pay. No hourly traps. No surprises. Just a fixed price and
        a team that gets the job done right.
      </Paragraph>

      <SectionTitle data-id="element-256">Who We Are</SectionTitle>
      <Paragraph data-id="element-257">
        Based in Tampa, FL, Move Fighters serves the entire Tampa Bay metro area
        and beyond — covering a 50-mile radius. Our team of 35+ professional
        movers operates a fleet of 68 trucks, completing over 10,500 successful
        moves and counting.
      </Paragraph>

      <SectionTitle data-id="element-258">Why "Fighters"?</SectionTitle>
      <Paragraph data-id="element-259">
        Because moving shouldn't feel like a disaster. We fight for your peace
        of mind. We fight against the scams, the hidden charges, and the
        careless handling that gives movers a bad name. When you book Move
        Fighters, you're booking a team that treats your belongings like their
        own.
      </Paragraph>

      <SectionTitle data-id="element-260">Our Values</SectionTitle>
      <ul className="mb-4" data-id="element-261">
        <BulletItem data-id="element-262">
          <strong className="text-white" data-id="element-263">Transparency:</strong> Fixed pricing
          with zero hidden fees. What you see is what you pay.
        </BulletItem>
        <BulletItem data-id="element-264">
          <strong className="text-white" data-id="element-265">Professionalism:</strong> Uniformed
          crews, branded trucks, protective equipment on every job.
        </BulletItem>
        <BulletItem data-id="element-266">
          <strong className="text-white" data-id="element-267">Speed:</strong> We move fast without
          cutting corners. Your time matters.
        </BulletItem>
        <BulletItem data-id="element-268">
          <strong className="text-white" data-id="element-269">Care:</strong> Every item is wrapped,
          padded, and handled with respect.
        </BulletItem>
        <BulletItem data-id="element-270">
          <strong className="text-white" data-id="element-271">Bilingual Service:</strong> We proudly
          serve Tampa's diverse community in English and Spanish.
        </BulletItem>
      </ul>
    </div>;
}
function FleetContent() {
  return <div data-id="element-272">
      <Paragraph data-id="element-273">
        Move Fighters operates one of the largest and most modern moving fleets
        in the Tampa Bay area. Every vehicle is professionally maintained, fully
        branded, and equipped with the tools needed to protect your belongings
        from door to door.
      </Paragraph>

      <SectionTitle data-id="element-274">Fleet Overview</SectionTitle>
      <ul className="mb-4" data-id="element-275">
        <BulletItem data-id="element-276">
          <strong className="text-white" data-id="element-277">68 Active Units:</strong> From compact
          trucks for studio apartments to 26-foot box trucks for full-home
          moves.
        </BulletItem>
        <BulletItem data-id="element-278">
          <strong className="text-white" data-id="element-279">GPS-Tracked:</strong> Every truck is
          monitored in real-time for efficient routing and on-time arrivals.
        </BulletItem>
        <BulletItem data-id="element-280">
          <strong className="text-white" data-id="element-281">Climate-Aware:</strong> Florida heat is
          no joke — our trucks are equipped to protect sensitive items during
          transit.
        </BulletItem>
        <BulletItem data-id="element-282">
          <strong className="text-white" data-id="element-283">Professional Equipment:</strong>{' '}
          Hydraulic lift gates, furniture dollies, moving blankets, shrink wrap,
          and specialty crating materials on every truck.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-284">Equipment Standards</SectionTitle>
      <Paragraph data-id="element-285">
        Every Move Fighters truck carries a full inventory of protective
        materials: premium moving blankets, stretch wrap, mattress bags, TV
        boxes, and furniture pads. We don't charge extra for these — they're
        included in every package because protecting your belongings is
        non-negotiable.
      </Paragraph>

      <SectionTitle data-id="element-286">Maintenance & Safety</SectionTitle>
      <Paragraph data-id="element-287">
        Our fleet undergoes regular maintenance inspections to ensure safety and
        reliability. Every vehicle is fully insured and DOT-compliant. When a
        Move Fighters truck pulls up to your home, you can trust it's ready for
        the job.
      </Paragraph>
    </div>;
}
function CoverageContent() {
  return <div data-id="element-288">
      <Paragraph data-id="element-289">
        Move Fighters proudly serves the greater Tampa Bay area and surrounding
        communities within a 50-mile radius of our Tampa headquarters. Whether
        you're moving across the street or across the county, we've got you
        covered.
      </Paragraph>

      <SectionTitle data-id="element-290">Primary Service Area</SectionTitle>
      <ul className="mb-4" data-id="element-291">
        <BulletItem data-id="element-292">
          <strong className="text-white" data-id="element-293">Tampa</strong> — Our home base and
          headquarters.
        </BulletItem>
        <BulletItem data-id="element-294">
          <strong className="text-white" data-id="element-295">St. Petersburg</strong> — Full coverage
          across the Pinellas Peninsula.
        </BulletItem>
        <BulletItem data-id="element-296">
          <strong className="text-white" data-id="element-297">Clearwater</strong> — Including
          Clearwater Beach and surrounding areas.
        </BulletItem>
        <BulletItem data-id="element-298">
          <strong className="text-white" data-id="element-299">Lakeland</strong> — East Polk County
          coverage.
        </BulletItem>
        <BulletItem data-id="element-300">
          <strong className="text-white" data-id="element-301">Bradenton & Sarasota</strong> — South
          of the Bay, fully covered.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-302">Extended Coverage</SectionTitle>
      <Paragraph data-id="element-303">
        We also serve Brandon, Riverview, Wesley Chapel, Lutz, Land O' Lakes,
        Plant City, Largo, New Port Richey, Spring Hill, Zephyrhills, Dade City,
        and dozens of smaller communities within our 50-mile radius. If both
        your pickup and delivery addresses are within our zone, you're good to
        go.
      </Paragraph>

      <SectionTitle data-id="element-304">Not Sure If You're Covered?</SectionTitle>
      <Paragraph data-id="element-305">
        Enter your addresses during the booking process and our system will
        automatically verify coverage. You can also call us at (813) 476-8935
        and we'll confirm your route in seconds.
      </Paragraph>
    </div>;
}
function CareersContent() {
  return <div data-id="element-306">
      <Paragraph data-id="element-307">
        Move Fighters is always looking for strong, reliable, and motivated
        individuals to join our growing team. If you thrive in a fast-paced
        environment and take pride in hard work, we want to hear from you.
      </Paragraph>

      <SectionTitle data-id="element-308">Why Work With Us?</SectionTitle>
      <ul className="mb-4" data-id="element-309">
        <BulletItem data-id="element-310">
          <strong className="text-white" data-id="element-311">Competitive Pay:</strong> We pay above
          industry average because we hire above-average people.
        </BulletItem>
        <BulletItem data-id="element-312">
          <strong className="text-white" data-id="element-313">Tips & Bonuses:</strong> Our crews
          regularly earn tips from satisfied customers, plus performance
          bonuses.
        </BulletItem>
        <BulletItem data-id="element-314">
          <strong className="text-white" data-id="element-315">Growth Opportunities:</strong> Start as
          a mover, grow into a crew lead, supervisor, or operations role.
        </BulletItem>
        <BulletItem data-id="element-316">
          <strong className="text-white" data-id="element-317">Team Culture:</strong> We're a
          tight-knit crew that works hard and supports each other.
        </BulletItem>
        <BulletItem data-id="element-318">
          <strong className="text-white" data-id="element-319">Steady Work:</strong> Tampa's moving
          market is year-round. No seasonal layoffs.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-320">Open Positions</SectionTitle>
      <ul className="mb-4" data-id="element-321">
        <BulletItem data-id="element-322">
          <strong className="text-white" data-id="element-323">Professional Movers</strong> —
          Full-time, physical role. Must be reliable and team-oriented.
        </BulletItem>
        <BulletItem data-id="element-324">
          <strong className="text-white" data-id="element-325">Truck Drivers</strong> — Valid CDL or
          clean driving record required. Box truck experience preferred.
        </BulletItem>
        <BulletItem data-id="element-326">
          <strong className="text-white" data-id="element-327">Crew Leaders</strong> — Experienced
          movers ready to lead a team and manage on-site operations.
        </BulletItem>
      </ul>

      <SectionTitle data-id="element-328">How to Apply</SectionTitle>
      <Paragraph data-id="element-329">
        Send your name, phone number, and a brief description of your experience
        to <strong className="text-white" data-id="element-330">careers@movefighters.com</strong> or
        call us at <strong className="text-white" data-id="element-331">(813) 476-8935</strong>.
        Walk-ins are also welcome at our Tampa location.
      </Paragraph>
    </div>;
}
function ContactUsContent() {
  return <div data-id="element-332">
      <Paragraph data-id="element-333">
        We're here to help — whether you have questions about our packages, need
        to reschedule, or just want to talk through your move. Reach out anytime
        through the channels below.
      </Paragraph>

      <SectionTitle data-id="element-334">Phone</SectionTitle>
      <Paragraph data-id="element-335">
        <a href="tel:8134768935" className="text-brand-yellow hover:text-white font-bold text-lg transition-colors" data-id="element-336">
          (813) 476-8935
        </a>
        <br data-id="element-337" />
        <span className="text-gray-500 text-xs" data-id="element-338">
          Mon–Sat: 7:00 AM – 8:00 PM | Sunday: 8:00 AM – 5:00 PM
        </span>
      </Paragraph>

      <SectionTitle data-id="element-339">Email</SectionTitle>
      <Paragraph data-id="element-340">
        <a href="mailto:info@movefighters.com" className="text-brand-yellow hover:text-white font-bold transition-colors" data-id="element-341">
          info@movefighters.com
        </a>
        <br data-id="element-342" />
        <span className="text-gray-500 text-xs" data-id="element-343">
          We respond within 2 hours during business hours.
        </span>
      </Paragraph>

      <SectionTitle data-id="element-344">Location</SectionTitle>
      <Paragraph data-id="element-345">
        Move Fighters LLC
        <br data-id="element-346" />
        Tampa, FL
        <br data-id="element-347" />
        Serving a 50-mile radius of the Tampa Bay area.
      </Paragraph>

      <SectionTitle data-id="element-348">Social Media</SectionTitle>
      <Paragraph data-id="element-349">
        Follow us on social media for moving tips, customer stories, and
        behind-the-scenes content from our rescue crews.
      </Paragraph>

      <div className="mt-6 p-4 bg-brand-red/5 border border-brand-red/20 rounded-xl" data-id="element-350">
        <p className="text-gray-400 text-xs leading-relaxed" data-id="element-351">
          <strong className="text-white" data-id="element-352">Emergency Moves:</strong> Need to move
          ASAP? Call us directly at (813) 476-8935. We accommodate last-minute
          rescues when availability allows (minimum 72-hour lead time for online
          bookings).
        </p>
      </div>
    </div>;
}
export function Footer() {
  const {
    t
  } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [activeModal, setActiveModal] = useState<'terms' | 'privacy' | 'cancellation' | 'about' | 'fleet' | 'coverage' | 'careers' | 'contactUs' | null>(null);
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };
  return <footer className="bg-[#050505] relative overflow-hidden" data-id="element-353">
      {/* CTA Banner */}
      <div className="bg-brand-red relative" data-id="element-354">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] opacity-30" data-id="element-355" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10" data-id="element-356">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-357">
            <div className="text-center md:text-left" data-id="element-358">
              <h3 className="font-oswald text-2xl md:text-3xl font-bold text-white uppercase" data-id="element-359">
                {t.footer.help}
              </h3>
              <p className="text-white/80 text-sm mt-1" data-id="element-360">{t.footer.licensed}</p>
            </div>
            <a href="tel:8134768935" className="inline-flex items-center gap-3 bg-white text-brand-red px-8 py-4 rounded-xl font-oswald font-bold text-xl uppercase tracking-wider hover:bg-brand-yellow hover:text-brand-black transition-all transform hover:scale-105 active:scale-95 shadow-lg" data-id="element-361">
              <PhoneIcon className="w-6 h-6 animate-pulse" data-id="element-362" />
              {t.footer.contact.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-363">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8" data-id="element-364">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-6" data-id="element-365">
            <div className="flex items-center gap-3" data-id="element-366">
              <img src="https://cdn.magicpatterns.com/uploads/uJZrGhTzMnY7pLTJd1VGay/IMG_4659.jpg" alt="Move Fighters Logo" className="h-14 w-auto object-contain rounded-lg" data-id="element-367" />
              <div data-id="element-368">
                <h4 className="font-oswald text-xl font-bold text-white uppercase" data-id="element-369">
                  Move Fighters
                </h4>
                <p className="text-gray-500 text-xs" data-id="element-370">{t.footer.tagline}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2" data-id="element-371">
              {t.footer.badges.map((badge: string, i: number) => <span key={i} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-gray-400" data-id="element-372">
                  <CheckIcon className="w-3 h-3 text-green-500" data-id="element-373" />
                  {badge}
                </span>)}
            </div>

            <div className="space-y-3" data-id="element-374">
              <h5 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-375">
                {t.footer.newsletter.title}
              </h5>
              <form onSubmit={handleSubscribe} className="flex gap-2" data-id="element-376">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.footer.newsletter.placeholder} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors" data-id="element-377" />
                <button type="submit" className="bg-brand-red hover:bg-brand-darkred text-white px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors flex-shrink-0" data-id="element-378">
                  {subscribed ? <CheckIcon className="w-5 h-5" data-id="element-379" /> : t.footer.newsletter.button}
                </button>
              </form>
              <p className="text-gray-600 text-xs" data-id="element-380">
                {t.footer.newsletter.note}
              </p>
            </div>
          </div>

          {/* Services Column */}
          <div className="space-y-4" data-id="element-381">
            <h5 className="font-oswald text-sm font-bold text-white uppercase tracking-wider border-b border-brand-red/30 pb-3" data-id="element-382">
              {t.footer.services.title}
            </h5>
            <ul className="space-y-2.5" data-id="element-383">
              {t.footer.services.items.map((item: string, i: number) => <li key={i} data-id="element-384">
                  <span className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer flex items-center gap-2" data-id="element-385">
                    <FireIcon className="w-3 h-3 text-brand-red/50" data-id="element-386" />
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Legal Column */}
          <div className="space-y-4" data-id="element-387">
            <h5 className="font-oswald text-sm font-bold text-white uppercase tracking-wider border-b border-brand-red/30 pb-3" data-id="element-388">
              {t.footer.legal.title}
            </h5>
            <ul className="space-y-2.5" data-id="element-389">
              <li data-id="element-390">
                <button onClick={() => setActiveModal('terms')} className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-2 w-full text-left" data-id="element-391">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-red/50" data-id="element-392" />
                  {t.footer.legal.terms}
                </button>
              </li>
              <li data-id="element-393">
                <button onClick={() => setActiveModal('privacy')} className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-2 w-full text-left" data-id="element-394">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-red/50" data-id="element-395" />
                  {t.footer.legal.privacy}
                </button>
              </li>
              <li data-id="element-396">
                <button onClick={() => setActiveModal('cancellation')} className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-2 w-full text-left" data-id="element-397">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-red/50" data-id="element-398" />
                  {t.footer.legal.cancellation}
                </button>
              </li>
              <li data-id="element-399">
                <span className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer flex items-center gap-2" data-id="element-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-red/50" data-id="element-401" />
                  {t.footer.legal.insurance}
                </span>
              </li>
            </ul>

            <h5 className="font-oswald text-sm font-bold text-white uppercase tracking-wider border-b border-brand-red/30 pb-3 pt-4" data-id="element-402">
              {t.footer.company.title}
            </h5>
            <ul className="space-y-2.5" data-id="element-403">
              {t.footer.company.items.map((item: string, i: number) => {
              const modalKeys = ['about', 'fleet', 'coverage', 'careers', 'contactUs'] as const;
              return <li key={i} data-id="element-404">
                    <button onClick={() => setActiveModal(modalKeys[i])} className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-2 w-full text-left" data-id="element-405">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow/50" data-id="element-406" />
                      {item}
                    </button>
                  </li>;
            })}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="space-y-4" data-id="element-407">
            <h5 className="font-oswald text-sm font-bold text-white uppercase tracking-wider border-b border-brand-red/30 pb-3" data-id="element-408">
              {t.footer.contact.title}
            </h5>
            <div className="space-y-4" data-id="element-409">
              <a href="tel:8134768935" className="flex items-start gap-3 group" data-id="element-410">
                <div className="w-9 h-9 rounded-lg bg-brand-red/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-red/20 transition-colors" data-id="element-411">
                  <PhoneIcon className="w-4 h-4 text-brand-red" data-id="element-412" />
                </div>
                <div data-id="element-413">
                  <p className="text-white font-bold text-sm group-hover:text-brand-yellow transition-colors" data-id="element-414">
                    {t.footer.contact.phone}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-415">{t.footer.callNow}</p>
                </div>
              </a>

              <a href="mailto:info@movefighters.com" className="flex items-start gap-3 group" data-id="element-416">
                <div className="w-9 h-9 rounded-lg bg-brand-yellow/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-yellow/20 transition-colors" data-id="element-417">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-brand-yellow" data-id="element-418">
                    <rect width="20" height="16" x="2" y="4" rx="2" data-id="element-419" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-420" />
                  </svg>
                </div>
                <div data-id="element-421">
                  <p className="text-white font-bold text-sm group-hover:text-brand-yellow transition-colors" data-id="element-422">
                    {t.footer.contact.email}
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-3" data-id="element-423">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0" data-id="element-424">
                  <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-425" />
                </div>
                <div data-id="element-426">
                  <p className="text-gray-300 text-sm" data-id="element-427">
                    {t.footer.contact.hours}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-428">
                    {t.footer.contact.hoursSun}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-429">
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0" data-id="element-430">
                  <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-431" />
                </div>
                <div data-id="element-432">
                  <p className="text-gray-300 text-sm" data-id="element-433">
                    {t.footer.contact.address}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10" data-id="element-434">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-435">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-436">
            <p className="text-gray-600 text-sm" data-id="element-437">
              &copy; {currentYear} Move Fighters LLC. {t.footer.rights}
            </p>
            <div className="flex items-center gap-6" data-id="element-438">
              <button onClick={() => setActiveModal('terms')} className="text-gray-600 text-xs hover:text-gray-400 transition-colors" data-id="element-439">
                {t.footer.legal.terms}
              </button>
              <span className="text-gray-700" data-id="element-440">|</span>
              <button onClick={() => setActiveModal('privacy')} className="text-gray-600 text-xs hover:text-gray-400 transition-colors" data-id="element-441">
                {t.footer.legal.privacy}
              </button>
              <span className="text-gray-700" data-id="element-442">|</span>
              <button onClick={() => setActiveModal('cancellation')} className="text-gray-600 text-xs hover:text-gray-400 transition-colors" data-id="element-443">
                {t.footer.legal.cancellation}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Modals */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms & Conditions" data-id="element-444">
        <TermsContent data-id="element-445" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'cancellation'} onClose={() => setActiveModal(null)} title="Cancellation & Refund Policy" data-id="element-446">
        <CancellationContent data-id="element-447" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" data-id="element-448">
        <PrivacyContent data-id="element-449" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'about'} onClose={() => setActiveModal(null)} title="About Move Fighters" data-id="element-450">
        <AboutContent data-id="element-451" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'fleet'} onClose={() => setActiveModal(null)} title="Our Fleet" data-id="element-452">
        <FleetContent data-id="element-453" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'coverage'} onClose={() => setActiveModal(null)} title="Coverage Area" data-id="element-454">
        <CoverageContent data-id="element-455" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'careers'} onClose={() => setActiveModal(null)} title="Careers" data-id="element-456">
        <CareersContent data-id="element-457" />
      </LegalModal>

      <LegalModal isOpen={activeModal === 'contactUs'} onClose={() => setActiveModal(null)} title="Contact Us" data-id="element-458">
        <ContactUsContent data-id="element-459" />
      </LegalModal>
    </footer>;
}